import {
  IMarketingCampaign,
  IMarketingCampaignStep,
  IMarketingCampaignStepConversion,
  MarketingCampaignEntryCollection,
  MarketingCampaignEntryStatus,
  MarketingCampaignStepConversionStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  collectionGroupQuery,
  DocumentReference,
  Firestore,
  IReffable,
  Timestamp,
  toQuery,
  toTimestamp,
  undeletedFilter,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { groupBy, sum, uniqBy } from 'lodash';
import * as moment from 'moment-timezone';
import { MarketingCampaignStepConversion } from './campaign-step-conversion';

export interface ICampaignCostForecast {
  campaignRef: DocumentReference<IMarketingCampaign>;
  windowStart: Timestamp;
  windowEnd: Timestamp;
  activeEntries: number;
  pendingSteps: number;
  estimatedSegments: number;
  estimatedCost: number;
  costBreakdown: ICampaignStepCostBreakdown[];
}

export interface ICampaignStepCostBreakdown {
  stepRef: DocumentReference<IMarketingCampaignStep>;
  stepName: string;
  entriesAffected: number;
  estimatedSegments: number;
  estimatedCost: number;
}

export class CampaignCostForecast {
  static async forecastCosts(
    campaign: IReffable<IMarketingCampaign>,
    windowStart: Timestamp,
    windowEnd: Timestamp
  ): Promise<ICampaignCostForecast> {
    const stepConversionQuery =
      collectionGroupQuery<IMarketingCampaignStepConversion>(
        MarketingCampaignEntryCollection.StepConversions,
        where('campaignRef', '==', campaign.ref)
      );
    const incompleteStatuses = [
      MarketingCampaignStepConversionStatus.Pending,
      MarketingCampaignStepConversionStatus.Queued,
    ];
    const incompleteInWindowQuery = toQuery(
      stepConversionQuery,
      where('status', 'in', incompleteStatuses),
      where('estimatedRunDate', '>=', windowStart),
      where('estimatedRunDate', '<=', windowEnd)
    );
    const stepConversions = (
      await Firestore.getDocs(incompleteInWindowQuery)
    ).filter(undeletedFilter);

    const activeStepConversions =
      await CampaignCostForecast._filterByActiveEntries(stepConversions);

    const costBreakdown = await CampaignCostForecast._buildCostBreakdown(
      activeStepConversions
    );

    const uniqueEntryRefs = uniqBy(
      activeStepConversions.map((conversion) =>
        MarketingCampaignStepConversion.entryRef(conversion)
      ),
      (ref) => ref.path
    );

    const totalEstimatedCost = sum(
      activeStepConversions.map((conversion) => conversion.estimatedCostRemaining)
    );

    const totalEstimatedSegments = sum(
      activeStepConversions.map((conversion) => conversion.estimatedSegments ?? 0)
    );

    const forecast = {
      campaignRef: campaign.ref,
      windowStart,
      windowEnd,
      activeEntries: uniqueEntryRefs.length,
      pendingSteps: activeStepConversions.length,
      estimatedSegments: totalEstimatedSegments,
      estimatedCost: totalEstimatedCost,
      costBreakdown,
    };

    return forecast;
  }

  static async forecastSubscription(
    campaigns: IReffable<IMarketingCampaign>[],
    windowHours: number = 24
  ): Promise<ICampaignCostForecast[]> {
    const windowStart = toTimestamp();
    const windowEnd = toTimestamp(moment().add(windowHours, 'hours'));
    return asyncForEach(campaigns, (campaign) =>
      CampaignCostForecast.forecastCosts(campaign, windowStart, windowEnd)
    );
  }

  private static async _filterByActiveEntries(
    stepConversions: WithRef<IMarketingCampaignStepConversion>[]
  ): Promise<WithRef<IMarketingCampaignStepConversion>[]> {
    const uniqueEntryRefs = uniqBy(
      stepConversions.map((conversion) =>
        MarketingCampaignStepConversion.entryRef(conversion)
      ),
      (ref) => ref.path
    );

    const entries = await asyncForEach(uniqueEntryRefs, (ref) =>
      Firestore.getDoc(ref)
    );

    const activeEntryPaths = new Set(
      entries
        .filter(undeletedFilter)
        .filter((entry) => entry.status === MarketingCampaignEntryStatus.Active)
        .map((entry) => entry.ref.path)
    );

    return stepConversions.filter((conversion) => {
      const entryPath =
        MarketingCampaignStepConversion.entryRef(conversion).path;
      return activeEntryPaths.has(entryPath);
    });
  }

  private static async _buildCostBreakdown(
    stepConversions: WithRef<IMarketingCampaignStepConversion>[]
  ): Promise<ICampaignStepCostBreakdown[]> {
    const grouped = groupBy(stepConversions, (conversion) => conversion.stepRef.path);

    return asyncForEach(
      Object.entries(grouped),
      async ([_stepPath, conversions]) => {
        const firstConversion = conversions[0];
        const step = await Firestore.getDoc(firstConversion.stepRef);

        const uniqueEntries = uniqBy(
          conversions.map((conversion) => MarketingCampaignStepConversion.entryRef(conversion)),
          (ref) => ref.path
        );

        const stepEstimatedSegments = sum(
          conversions.map((conversion) => conversion.estimatedSegments ?? 0)
        );
        const stepEstimatedCost = sum(
          conversions.map((conversion) => conversion.estimatedCostRemaining)
        );

        return {
          stepRef: firstConversion.stepRef,
          stepName: step.name,
          entriesAffected: uniqueEntries.length,
          estimatedSegments: stepEstimatedSegments,
          estimatedCost: stepEstimatedCost,
        };
      }
    );
  }
}
